import Button from 'components/Common/Button';
import Header from 'components/Layouts/components/Header';
import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { Avatar, Box, styled, Typography } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Paper from '@mui/material/Paper';
import { useGetCurrentUser, useUpdateCurrentUser } from 'apis/user/hook';
import Spinner from 'components/Common/Spinner';
import TelInput from 'components/Common/TelInput';

export default function UserProfile() {
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { mutateAsync: updateUser, isPending } = useUpdateCurrentUser();
  const { mutateAsync: updateUserImage } = useUpdateCurrentUser(true);

  const { handleSubmit, control } = useForm({
    values: { profile: currentUser }
  });

  const [avatarUrl, setAvatarUrl] = useState<string | null>(currentUser?.avatar_url ? currentUser.avatar_url : null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarUrl(newUrl);
      const formData = new FormData();
      formData.append('profile[avatar]', file);
      await updateUserImage(formData);
    }
  };

  const onSubmit = (data: any) => {
    updateUser(data);
  };

  if (isCurrentUserLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Header />
      <Box component='main' sx={{ flexGrow: 1, padding: 3, paddingTop: '150px', width: '50%' }} marginX='auto'>
        <Typography variant='h5' fontWeight='600'>
          Account Details
        </Typography>
        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Paper
            elevation={1}
            sx={{
              border: '1px solid #F3F3F3',
              padding: '24px',
              borderRadius: '4px',
              marginTop: '24px'
            }}
          >
            <Typography>
              Avatar
              <Typography color='red' component='span' sx={{ display: 'inline' }}>
                *
              </Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '16px',
                alignItems: 'center',
                marginTop: '8px'
              }}
            >
              <Avatar
                src={avatarUrl || '/path/to/default/avatar.png'}
                alt={currentUser?.name ?? 'User Avatar'}
                sx={{ width: 64, height: 64 }}
              />
              <Button component='label' role={undefined} variant='outlined' tabIndex={-1}>
                Choose files
                <VisuallyHiddenInput
                  type='file'
                  onChange={handleFileChange}
                  accept='image/png, image/jpeg'
                  multiple={false}
                />
              </Button>
              <Typography variant='body2' color='secondary'>
                accepted formats: .png .jpg
              </Typography>
            </Box>
            <Grid container spacing='26px' marginTop='24px'>
              <Grid size={6}>
                <Input name='profile.name' label='Name' control={control} fullWidth />
              </Grid>
              <Grid size={6}>
                <Input
                  name='profile.email'
                  type='email'
                  label='Email address'
                  control={control}
                  fullWidth
                  disabled
                  required
                />
              </Grid>
              <Grid size={6}>
                <TelInput
                  name='profile.phone_number'
                  label='Phone Number'
                  control={control}
                  defaultCountry='US'
                  preferredCountries={['US', 'CA', 'IN']}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                marginTop: '24px'
              }}
            >
              <Button type='submit' sx={{ display: 'block', marginLeft: 'auto' }} isPending={isPending}>
                Save Changes
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
